/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

export const isDescendant = (parent, child) => {
  if (parent && child) {
    var node = child.parentNode;
    while (node) {
      if (node === parent) {
        return true;
      }
      node = node.parentNode;
    }
  }
  return false;
};

export const focusNextElement = (element, isForwardDirection = false) => {
  var focussableElements =
    'a:not([disabled]), button:not([disabled]), input[type=text]:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';
  if (document.activeElement) {
    var focussable = Array.prototype.filter.call(
      element.querySelectorAll(focussableElements),
      element =>
        // check for visibility while always include the current activeElement
        element.offsetWidth > 0 || element.offsetHeight > 0 || element === document.activeElement
    );

    var index = focussable.indexOf(document.activeElement);
    if (index > -1) {
      var nextElement;
      if (isForwardDirection) nextElement = focussable[index + 1];
      else nextElement = focussable[index - 1];
      if (nextElement) {
        nextElement.focus();
        return true;
      }
    }
  }
  return false;
};

export const focusMainElement = () => {
  const mainElement = document.querySelector('[role="main"]');
  if (mainElement) mainElement.focus();
};

export const getScrollBarSize = () => {
  // Using an iframe so there are no conflicts with any css in the current frame.
  const iframe = document.createElement("iframe");

  if (document.body) {
    document.body.appendChild(iframe);
    const contentDocument = iframe.contentDocument || iframe.contentWindow.document;

    const outer = contentDocument.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.width = "100px";
    outer.style.msOverflowStyle = "scrollbar";

    contentDocument.body.appendChild(outer);

    const widthNoScroll = outer.offsetWidth;
    // force scrollbars
    outer.style.overflow = "scroll";

    // add innerdiv
    const inner = contentDocument.createElement("div");
    inner.style.width = "100%";
    outer.appendChild(inner);

    const widthWithScroll = inner.offsetWidth;

    // remove divs
    outer.parentNode.removeChild(outer);
    iframe.parentNode.removeChild(iframe);

    return widthNoScroll - widthWithScroll;
  }
  return 0;
};

export const isDialog = element => {
  if (!(element instanceof HTMLElement)) return false;
  return element.classList.contains("spectrum-Dialog");
};

export const isTextArea = element => {
  if (!(element instanceof HTMLTextAreaElement)) return false;
  return true;
};

export const isInput = element => {
  if (!(element instanceof HTMLInputElement)) return false;
  return true;
};

export const isButton = element => {
  const isButtonElement = element instanceof HTMLButtonElement;
  const isCheckbox = element.getAttribute("role") && element.getAttribute("role") === "checkbox";
  const isRadioButton = element.getAttribute("role") && element.getAttribute("role") === "radio";
  return isButtonElement || isCheckbox || isRadioButton;
};

export const isSelectElement = element => {
  if (!(element instanceof HTMLSelectElement)) return false;
  return true;
};

export const getHitElement = (elemClass, x, y) => {
  const elements = document.querySelectorAll(`.${elemClass}`);
  for (var j = 0; j < elements.length; j++) {
    const elemRect = elements[j].getBoundingClientRect();
    if (
      elemRect.left < x - document.documentElement.scrollLeft &&
      elemRect.right > x - document.documentElement.scrollLeft &&
      elemRect.top < y - document.documentElement.scrollTop &&
      elemRect.bottom > y - document.documentElement.scrollTop
    )
      return elements[j];
  }
  return undefined;
};

export const parentVisitor = (element, callback) => {
  if (element) {
    if (callback(element)) {
      return true;
    }
    return parentVisitor(element.parentNode, callback);
  }
  return false;
};

export const isInputOrEditableDiv = activeElement => {
  let inputTypes = ["email", "text", "password", "search", "tel", "url"];
  let isInputElement = activeElement.nodeName === "INPUT" && inputTypes.indexOf(activeElement.type) !== -1;
  let isTextArea = activeElement.nodeName === "TEXTAREA";
  let isSelectList = activeElement.nodeName === "SELECT";
  let isEditableDiv = false;
  if ("hasAttribute" in activeElement && activeElement.hasAttribute("contenteditable")) {
    let attrVal = activeElement.getAttribute("contenteditable");
    isEditableDiv = attrVal === "true" || attrVal === "" || attrVal === "plaintext-only";
  }
  return isInputElement || isEditableDiv || isTextArea || isSelectList;
};

export const isBody = element => element.tagName === "BODY";
